import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';
import logoblue from '../assets/logo.png';
import ContactModal from '../components/ContactModal';

function useScrollReveal() {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) el.classList.add('revealed'); },
            { threshold: 0.12 }
        );
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    return ref;
}

function Reveal({ children, className = '', delay = 0, tag: Tag = 'div' }) {
    const ref = useScrollReveal();
    return (
        <Tag ref={ref} className={`reveal ${className}`} style={{ transitionDelay: `${delay}ms` }}>
            {children}
        </Tag>
    );
}

const About = () => {
    const [modalOpen, setModalOpen] = useState(false);
    const openModal = useCallback((e) => { e.preventDefault(); setModalOpen(true); }, []);
    const closeModal = useCallback(() => setModalOpen(false), []);
    const [navOpen, setNavOpen] = useState(false);
    const closeNav = useCallback(() => setNavOpen(false), []);

    return (
        <div className="lp-root">

            {/* ── NAV ── */}
            <header className="lp-nav">
                <div className="lp-container lp-nav__inner">
                    <Link to="/" className="lp-nav__brand" onClick={closeNav}>
                        <img src={logoblue} alt="Iron Ridge Cyber" className="lp-nav__logo" />
                        <span className="lp-nav__wordmark">
                            Iron Ridge Cyber
                            <em>QuickChain</em>
                        </span>
                    </Link>
                    <nav className="lp-nav__links">
                        <Link to="/" className="lp-nav__link">Home</Link>
                        <Link to="/about" className="lp-nav__link lp-nav__link--active">About</Link>
                        <Link to="/#pricing" className="lp-nav__link">Pricing</Link>
                        <button onClick={openModal} className="lp-btn lp-btn--sm lp-btn--primary">Get Started</button>
                    </nav>
                    <button
                        className={`lp-hamburger${navOpen ? ' lp-hamburger--open' : ''}`}
                        onClick={() => setNavOpen(o => !o)}
                        aria-label="Toggle navigation"
                    >
                        <span /><span /><span />
                    </button>
                </div>
                <div className={`lp-mobile-nav${navOpen ? ' lp-mobile-nav--open' : ''}`}>
                    <Link to="/" className="lp-mobile-nav__link" onClick={closeNav}>Home</Link>
                    <Link to="/about" className="lp-mobile-nav__link" onClick={closeNav}>About</Link>
                    <Link to="/#pricing" className="lp-mobile-nav__link" onClick={closeNav}>Pricing</Link>
                    <button onClick={() => { closeNav(); openModal(); }} className="lp-btn lp-btn--primary" style={{ marginTop: '8px' }}>Get Started</button>
                </div>
            </header>

            {/* ── PAGE HEADER ── */}
            <section className="lp-about-hero">
                <div className="lp-container">
                    <Reveal>
                        <div className="lp-section-label">Company</div>
                        <h1 className="lp-about-h1">About Iron Ridge Cyber</h1>
                        <p className="lp-about-lead">
                            We build technology that bridges the gap between software engineering speed
                            and rigorous enterprise security standards.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* ── MISSION ── */}
            <section className="lp-section lp-section--alt">
                <div className="lp-container">
                    <Reveal>
                        <div className="lp-section-label">Mission</div>
                        <h2 className="lp-section-h2">Why We Built This</h2>
                        <p className="lp-about-lead" style={{ maxWidth: '720px', marginTop: '1rem' }}>
                            At Iron Ridge Cyber, our mission is to eliminate the documentation friction
                            that blocks otherwise-ready SaaS companies from closing enterprise deals.
                        </p>
                        <p className="lp-about-lead" style={{ maxWidth: '720px', marginTop: '1rem' }}>
                            Software supply chain verification is a well-defined, repeatable problem.
                            It should not require weeks of senior engineering time and manual spreadsheet
                            work every time a procurement team asks. We engineered QuickChain to automate
                            the structured documentation layer — SBOM generation, vulnerability summaries,
                            executive overviews — so your team stays focused on the product.
                        </p>
                    </Reveal>
                </div>
            </section>

            {/* ── HOW WE WORK ── */}
            <section className="lp-section">
                <div className="lp-container">
                    <Reveal>
                        <div className="lp-section-label">Approach</div>
                        <h2 className="lp-section-h2">How We Work</h2>
                        <p className="lp-section-desc">
                            QuickChain is not a self-serve automation product. It is a focused tool backed by direct engineering support.
                        </p>
                    </Reveal>

                    <div className="lp-narrative-grid">
                        <Reveal delay={0} className="lp-narrative-block">
                            <div className="lp-narrative-num">01</div>
                            <h3>We start by understanding your specific audit</h3>
                            <p>
                                Enterprise procurement requirements vary by sector. A healthcare system asking for an SBOM
                                has different expectations than a federal agency or a Fortune 500 bank. We review your
                                specific procurement request before generating output, so the format and detail level match
                                what your auditor will actually accept.
                            </p>
                        </Reveal>
                        <Reveal delay={80} className="lp-narrative-block">
                            <div className="lp-narrative-num">02</div>
                            <h3>We generate, then we explain</h3>
                            <p>
                                The SBOM and vulnerability summary are machine-generated and deterministic. The executive
                                risk overview is structured for a non-technical reader. We deliver both layers — the
                                technical artifact for the security team and the human-readable summary for the procurement
                                committee — because both audiences are in the room when the deal closes.
                            </p>
                        </Reveal>
                        <Reveal delay={160} className="lp-narrative-block">
                            <div className="lp-narrative-num">03</div>
                            <h3>We stay available through the review</h3>
                            <p>
                                Most vendors disappear after delivery. We include direct onboarding support and first-audit
                                guidance in every plan. If your procurement contact has a question about the output, you
                                have someone to call who built it and can explain every line.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>


            {/* ── CTA ── */}
            <section className="lp-cta-section">
                <div className="lp-container lp-cta-inner">
                    <Reveal>
                        <h2 className="lp-cta-h2">Ready to accelerate your next security review?</h2>
                    </Reveal>
                    <Reveal delay={100}>
                        <button onClick={openModal} className="lp-btn lp-btn--primary lp-btn--lg lp-btn--inverted">
                            Generate Your First SBOM
                        </button>
                    </Reveal>
                </div>
            </section>

            {/* ── FOOTER ── */}
            <footer className="lp-footer">
                <div className="lp-container lp-footer__inner">
                    <div className="lp-footer__brand">
                        <img src={logoblue} alt="Iron Ridge Cyber" className="lp-nav__logo" />
                        <span>Iron Ridge Cyber</span>
                    </div>
                    <div className="lp-footer__meta">
                        <span>© 2026 Iron Ridge Cyber Inc. All rights reserved.</span>
                        <span className="lp-footer__sep">·</span>
                        <span>Legal</span>
                        <span className="lp-footer__sep">·</span>
                        <span>Privacy Policy</span>
                    </div>
                </div>
            </footer>

            <ContactModal isOpen={modalOpen} onClose={closeModal} />

        </div>
    );
};

export default About;
